"use client";

import { Star, Users, BarChart3, Handshake, type LucideIcon } from "lucide-react";

import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Reveal } from "@/components/motion/Reveal";
import { getPartners } from "@/content/partners";
import { useLocale } from "@/lib/i18n/provider";
import type { PartnerBenefit } from "@/content/partners";

const benefitIcons: Record<PartnerBenefit["icon"], LucideIcon> = {
  Star,
  Users,
  BarChart3,
  Handshake,
};

export function PartnerBenefits() {
  const { locale } = useLocale();
  const copy = getPartners(locale);

  return (
    <section className="relative py-24 md:py-32">
      {/* subtle left-biased glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 top-1/2 -z-10 h-[500px] w-[500px] -translate-y-1/2 rounded-full blur-[140px] opacity-30"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(var(--glow-accent-rgb), 0.5) 0%, rgba(var(--glow-accent-rgb), 0) 70%)",
        }}
      />

      <Container>
        <div className="mx-auto max-w-[60ch] text-center">
          <Reveal delay={0}>
            <Eyebrow>{copy.benefits.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.07}>
            <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.06] tracking-[-0.025em] text-[var(--color-text-primary)]">
              {copy.benefits.title}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-5 text-[clamp(1rem,1.2vw,1.125rem)] leading-relaxed text-[var(--color-text-secondary)]">
              {copy.benefits.body}
            </p>
          </Reveal>
        </div>

        <ul className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {copy.benefits.items.map((benefit, i) => {
            const Icon = benefitIcons[benefit.icon];
            return (
              <Reveal key={benefit.title} delay={0.05 * i}>
                <li className="group flex h-full flex-col gap-5 rounded-3xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-7 transition-colors hover:border-[var(--color-accent-base)]/40">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--color-accent-soft)] text-[var(--color-accent-base)]">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <div className="flex flex-col gap-2.5">
                    <h3 className="text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
                      {benefit.title}
                    </h3>
                    <p className="text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
                      {benefit.body}
                    </p>
                  </div>
                </li>
              </Reveal>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}

export default PartnerBenefits;
